<template>
    <ValidationObserver ref="getQuoteForm">
        <div class="ls-get-quote-form__container">
            <form @submit.prevent="submitQuote">
                <ls-panel class="ls-get-quote-form__panel">
                    <div slot="header" class="ls-align--center ls-get-quote-form__panel-header">
                        <span
                            v-if="!hideNavigationHeader"
                            v-ls-track.click="{ target: 'header-logo' }"
                            class="ls-header__logo"
                            :class="{
                                wordmark: !isLawnStarter,
                                lawnstarter: isLawnStarter,
                                lawnlove: isLawnLove,
                                homegnome: isHomeGnome,
                            }"
                        >
                            <img
                                v-if="mobile"
                                :src="isLawnStarter ? lsLogo : ''"
                                width="161"
                                height="31"
                                :title="`${brand} Home Screen`"
                                :alt="`${brand} Home Screen`"
                            />
                            <img
                                v-else
                                :src="isLawnStarter ? lsLogo : ''"
                                width="208"
                                height="40"
                                :title="`${brand} Home Screen`"
                                :alt="`${brand} Home Screen`"
                            />
                        </span>
                        <p class="ls-color--green ls-condensed" style="padding: 0 10px;">
                            To get a quote for lawn service, enter your
                            <span class="ls-nowrap">information below</span>
                        </p>
                    </div>

                    <div class="grid ls-get-quote-form__panel-content">
                        <div class="grid__col-12 ls-margin--top">
                            <ls-google-autocomplete
                                ref="googleAutocomplete"
                                v-model="address.googlePlace.formatted_address"
                            ></ls-google-autocomplete>
                        </div>
                        <div v-if="!showPhone || !showEmail" class="grid__col-sm-6">
                            <label class="ls-label ls-margin--top" for="name">Your Name</label>
                            <div class="ls-input__wrapper">
                                <ValidationProvider
                                    v-slot="{ classes }"
                                    ref="name"
                                    rules="required|min:4"
                                >
                                    <span
                                        class="ls-input__icon"
                                        :class="classes"
                                        aria-hidden="true"
                                    >
                                        <i class="fa fa-child fa-fw fa-lg"></i>
                                    </span>
                                    <input
                                        id="name"
                                        v-model.trim="name"
                                        v-ls-track.change="{
                                            target: 'get-pricing-form--name-input',
                                            attachValue: true,
                                        }"
                                        type="text"
                                        maxlength="50"
                                        class="ls-input"
                                        :class="classes"
                                        name="name"
                                        autocomplete="name"
                                        @input="checkNameValidation"
                                    />
                                </ValidationProvider>
                            </div>
                        </div>
                        <div v-if="showPhone" class="grid__col-sm-6">
                            <label class="ls-label ls-margin--top" for="tel">
                                Phone Number&nbsp;<span class="ls-color--gray--light">*</span>
                            </label>
                            <div class="ls-input__wrapper">
                                <ValidationProvider
                                    v-slot="{ classes }"
                                    ref="phone"
                                    rules="required|isPhone"
                                >
                                    <span
                                        class="ls-input__icon"
                                        :class="classes"
                                        aria-hidden="true"
                                    >
                                        <i class="fa fa-phone fa-fw fa-lg"></i>
                                    </span>
                                    <ls-cleave
                                        id="tel"
                                        v-model="phone"
                                        v-ls-track.change="{
                                            target: 'get-pricing-form--phone-input',
                                            attachValue: true,
                                        }"
                                        :options="{
                                            phone: true,
                                            phoneRegionCode: 'US',
                                            delimiter: '-',
                                        }"
                                        type="tel"
                                        maxlength="50"
                                        class="ls-input"
                                        :class="classes"
                                        name="tel"
                                        autocomplete="tel"
                                        @input="checkPhoneValidation"
                                    />
                                </ValidationProvider>
                            </div>
                        </div>
                        <div v-if="showEmail" class="grid__col-sm-6">
                            <label class="ls-label ls-margin--top" for="email">Email</label>
                            <div class="ls-input__wrapper">
                                <ValidationProvider
                                    v-slot="{ classes }"
                                    ref="email"
                                    :rules="{
                                        required: true,
                                        email: true,
                                        isNotDuplicateEmail: {
                                            duplicateEmail,
                                        },
                                    }"
                                >
                                    <span
                                        class="ls-input__icon"
                                        :class="classes"
                                        aria-hidden="true"
                                    >
                                        <i class="fa fa-envelope-o fa-fw fa-lg"></i>
                                    </span>
                                    <input
                                        id="email"
                                        v-model.trim="email"
                                        v-ls-track.change="{
                                            target: 'get-pricing-form--email-input',
                                            attachValue: true,
                                        }"
                                        type="email"
                                        maxlength="50"
                                        class="ls-input"
                                        :class="classes"
                                        name="email"
                                        autocomplete="email"
                                        @input="checkEmailValidation"
                                    />
                                </ValidationProvider>
                            </div>
                        </div>
                    </div>

                    <div slot="button">
                        <div class="grid ls-get-quote-form__panel-button">
                            <ls-error v-if="displayError" class="ls-margin--bottom"></ls-error>

                            <div class="grid__grid__col-12">
                                <button
                                    v-ls-track.click="{
                                        target: 'get-pricing-form--get-quote-submit-button',
                                    }"
                                    :class="{
                                        'ls-button ls-button--block ls-button--lg': true,
                                        'll-button-primary': isLawnLove || isHomeGnome,
                                        'ls-button--yellow': isLawnStarter,
                                    }"
                                    type="submit"
                                >
                                    Get Your Quote&nbsp;<i class="fa fa-arrow-right"></i>
                                </button>
                                <p
                                    class="ls-align--justify ls-color--gray--light"
                                    data-font-size="-2"
                                >
                                    By submitting my contact information, I agree that
                                    {{ brand }} or its representatives may contact me by email,
                                    phone, or SMS at the email address or phone number I provide,
                                    including for marketing purposes and by automatic dialing
                                    systems. Message and data rates may apply. Message frequency
                                    varies. Text HELP for help. Text STOP to unsubscribe. I also
                                    agree to {{ brand }}’s
                                    <a :href="links.terms" target="_blank">terms of service</a>
                                    and <a :href="links.privacy" target="_blank">privacy policy</a>.
                                </p>
                            </div>
                        </div>
                    </div>
                </ls-panel>
            </form>
        </div>
    </ValidationObserver>
</template>

<script>
import VueScrollTo from 'vue-scrollto';
import _ from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import * as types from '@/store/types';
import lsPanel from '@/components/original-mydot/panel/Panel.vue';
import lsCleave from '@/components/original-mydot/inputs/cleave/Cleave.vue';
import lsGoogleAutocomplete from '@/components/original-mydot/google-components/GoogleAutocomplete.vue';
import LsLogo from '@/assets/lawnstarter-logo.png';
import brandMixin from '@/mixins/brand.mixin';
import lsError from '@/components/original-mydot/error/Error.vue';

export default {
    components: {
        lsPanel,
        lsCleave,
        lsGoogleAutocomplete,
        lsError,
    },
    mixins: [brandMixin],
    data() {
        return {
            name: '',
            phone: '',
            email: '',
            showPhone: true,
            showEmail: false,
            duplicateEmail: '',
            lsLogo: LsLogo,
            widgetId: '',
        };
    },
    computed: {
        ...mapState(['address', 'error', 'getPricing', 'loading', 'order']),
        ...mapGetters({
            mobile: types.SCREEN_SIZE__GET_MOBILE_SCREEN,
        }),
        displayError() {
            return this.address.showStandaloneStreetNumber || this.error.message;
        },
        hideNavigationHeader() {
            // if utm_source === 'customer-mobile', we are coming from RN WebView - hide nav
            return this.order.utm_source === 'customer-mobile';
        },
    },
    created() {
        this.showPhone = _.get(this.getPricing, 'showPhone');
        this.showEmail = _.get(this.getPricing, 'showEmail');
        this.name = _.get(this.order, 'name') || this.name;
        this.phone = _.get(this.order, 'phone') || this.phone;
        this.email = _.get(this.order, 'email') || this.email;
        if (_.get(this.error, 'field_messages.email')) {
            this.duplicateEmail = this.email;
            // want to make sure we are showing email if the error received is due to email
            this.showEmail = true;
        }
    },
    methods: {
        ...mapActions({
            orderUpdateName: types.ORDER__UPDATE_NAME,
            orderUpdatePhone: types.ORDER__UPDATE_PHONE,
            orderUpdateEmail: types.ORDER__UPDATE_EMAIL,
            orderCreateQuote: types.ORDER__CREATE_QUOTE,
        }),
        submitQuote() {
            this.$refs.googleAutocomplete.validateAddress();
            this.$refs.getQuoteForm.validate().then(() => {
                this.$nextTick(() => {
                    const firstInvalidInput =
                        document.getElementsByClassName('ls-invalid')[0] || null;

                    if (firstInvalidInput) {
                        VueScrollTo.scrollTo(firstInvalidInput, 500, { offset: -30 });
                        return;
                    }

                    this.orderCreateQuote({ address: this.address, type: this.order.intent });
                    window.scrollTo(0, 0);
                });
            });
        },
        checkNameValidation() {
            this.$nextTick(() => {
                this.$refs.name.validateSilent().then(({ valid }) => {
                    this.orderUpdateName({ name: this.name, invalid: !valid });
                });
            });
        },
        checkPhoneValidation() {
            this.$nextTick(() => {
                this.$refs.phone.validateSilent().then(({ valid }) => {
                    this.orderUpdatePhone({ phone: this.phone, invalid: !valid });
                });
            });
        },
        checkEmailValidation() {
            this.$nextTick(() => {
                this.$refs.email.validateSilent().then(({ valid }) => {
                    this.orderUpdateEmail({ email: this.email, invalid: !valid });
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-get-quote-form__panel {
    margin-top: rem(5);
    // match existing my.
    border-radius: rem(-5);
    border: 1px solid palette(gray);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.ls-get-quote-form__panel-header {
    p {
        font-weight: 700;
        font-size: rem(1);
        margin-top: rem(-4);
        margin-bottom: rem(0);
    }

    margin-top: -(rem(-5));
}

.ls-get-quote-form__panel-content {
    @include min-screen(breakpoint(sm)) {
        padding: rem(3) rem(6);
    }
}

.ls-get-quote-form__panel-button {
    @include max-screen(breakpoint(xs, max)) {
        margin-top: rem(6);
    }

    @include min-screen(breakpoint(sm)) {
        padding: rem(0) rem(6);
    }
}
.themed-app {
    .wordmark {
        content: $theme-logo;
        &.lawnlove {
            height: 31px;
            margin: 10px 0 10px;
        }
    }
    .ls-get-quote-form__panel {
        border-color: $theme-color-gray-dark;
        box-shadow: 0 5px 15px rgba($theme-color-black, 0.2);
    }
}
</style>
